import { defineStore, storeToRefs } from 'pinia';
import { v4 as uuid } from 'uuid';
import { computed, ref, watch } from 'vue';

import type {
  MessageAttachmentDTO,
  MessageCreatePayload,
  MessageResultDTO,
  MessageResultV2DTO,
  MessageUpdatePayload,
  ReactionAggregateResultDTO,
  ReactionCreatePayload,
  ReactionResultDTO,
} from '@nora-monorepo/nora-api-client';
import { useApiStore } from '@nora-monorepo/ui-shared';

import type { EditMessage, MessageInput, MessageState } from '#nora-chat-ui/states';
import { useAttachmentStore } from '#nora-chat-ui/stores/attachment-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useScrollStore } from '#nora-chat-ui/stores/scroll-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { convertToMessageResultV2 } from '#nora-chat-ui/utils/message-dto-adapter';

const MARK_AS_READ_TIMEOUT = 1500;

interface TimerList {
  [key: string]: ReturnType<typeof setTimeout>;
}

export const useConversationMessageStore = defineStore(
  'conversationMessageStore',
  () => {
    const quoteMessage = ref<MessageState>();
    const editingMessage = ref<EditMessage>();

    const userStore = useChatUserStore();

    const { user } = storeToRefs(userStore);

    const conversationStore = useConversationStore();
    const api = useApiStore();
    const filesApiStore = useAttachmentStore();
    const scrollStore = useScrollStore();

    const markAsReadTimers: TimerList = {};

    const activeMessageActionsId = ref<string>();
    const openContextMenuId = ref<string>();

    const setActiveMessageActionsId = (id?: string): void => {
      activeMessageActionsId.value = id;
    };

    const setOpenContextMenuId = (id?: string): void => {
      openContextMenuId.value = id;
    };

    // Initialize scroll state for open conversations
    watch(
      () => conversationStore.getOpenConversations,
      (openConversations) => {
        if (!openConversations) {
          return;
        }

        scrollStore.initializeScrollStateForConversations(openConversations);
      },
      {
        immediate: true,
      },
    );

    const getConversationMessage = computed(() => {
      return (conversationId: string, messageId: string) => {
        const conversation = conversationStore.getConversation(conversationId);

        if (!conversation) {
          return undefined;
        }

        return conversation.messages.find(
          messageState => messageState.message.id === messageId,
        );
      };
    });

    function createMessageObject(
      conversationId: string,
      message?: string | null,
      forwardedFromId?: string,
      attachments?: MessageAttachmentDTO[],
    ): MessageResultV2DTO {
      if (!user.value) {
        throw new Error('Tried to create a message without a loaded user.');
      }

      let quotedFrom: MessageResultDTO | undefined;

      if (quoteMessage.value) {
        quotedFrom = {
          id: quoteMessage.value.message.id,
          message: quoteMessage.value.message.message,
          sentBy: quoteMessage.value.message.sentBy,
          createdAt: quoteMessage.value.message.createdAt,
          sentByUserId: quoteMessage.value.message.sentBy.id,
          conversationId: quoteMessage.value.message.conversationId,
          isEdited: quoteMessage.value.message.isEdited ?? false,
        };
      }

      const messageData: MessageResultDTO = {
        id: uuid(),
        message,
        conversationId,
        sentByUserId: user.value.id,
        sentBy: user.value,
        createdAt: new Date(),
        updatedAt: undefined,
        deletedAt: undefined,
        reactions: [],
        readMessages: [],
        quotedFrom,
        forwardedFromId,
        isEdited: false,
        attachments: attachments?.map(attachment => ({
          ...attachment,
          size: `${attachment.size}`,
        })),
      };

      return convertToMessageResultV2(messageData);
    }

    async function createMessage(
      conversationId: string,
      message: string,
      forwardedFromId?: string,
      attachments?: MessageAttachmentDTO[],
    ): Promise<void> {
      if (attachments?.length && attachments?.length > 0) {
        filesApiStore.clearUploadsForConversation(conversationId);
      }

      const newMessage: MessageState = {
        message: createMessageObject(
          conversationId,
          message,
          forwardedFromId,
          attachments,
        ),
        status: 'sending',
      };

      conversationStore.addMessageToConversation(conversationId, newMessage);

      const payload: MessageCreatePayload = {
        id: newMessage.message.id,
        message,
        quotedMessageId: quoteMessage.value?.message.id,
        attachments,
      };

      quoteMessage.value = undefined;

      const response = await api.unwrap(
        api.conversationMessage.createConversationMessage({
          conversationId: newMessage.message.conversationId,
          messageCreatePayload: payload,
        }),
      );

      const resultMessage = response.data;

      if (!resultMessage) {
        newMessage.status = 'error';
        conversationStore.updateMessage(newMessage);

        // @todo add error indication and option to retry sending (perhaps also automatic retries?)
        throw new Error('Did not receive a result message.');
      }

      newMessage.message.createdAt = new Date(resultMessage.createdAt);
      newMessage.status = 'sent';

      conversationStore.updateMessage(newMessage);

      // Remove the sending status after a while
      setTimeout(() => {
        const message = getConversationMessage.value(
          conversationId,
          newMessage.message.id,
        );

        if (!message) {
          return;
        }

        message.status = undefined;
        conversationStore.updateMessage(message);
      }, 3000);
    }

    async function editMessage(message: string): Promise<void> {
      if (!editingMessage.value) {
        throw new Error('No edited messages');
      }

      const { oldMessage } = editingMessage.value;

      const payload: MessageUpdatePayload = {
        message,
      };

      const updatedMessage = { ...oldMessage };
      updatedMessage.message.message = message;
      updatedMessage.message.isEdited = true;
      editingMessage.value = undefined;

      await api.conversationMessage.updateConversationMessage({
        conversationId: updatedMessage.message.conversationId,
        messageId: updatedMessage.message.id,
        messageUpdatePayload: payload,
      });
    }

    function isEditingConversation(conversationId: string): boolean {
      return editingMessage.value?.oldMessage.message.conversationId === conversationId;
    }

    async function setMessageIsInView(
      messageToUpdate: MessageState,
      isInView: boolean,
    ): Promise<void> {
      const conversationToUpdate = conversationStore.getConversation(
        messageToUpdate.message.conversationId,
      );

      if (!conversationToUpdate) {
        throw new Error('Could not find the message to update.');
      }

      if (!user.value) {
        throw new Error('Could not find the user to update.');
      }

      messageToUpdate.isInView = isInView;

      const alreadyRead = messageToUpdate.message.isRead;

      if (isInView && !alreadyRead) {
        if (markAsReadTimers[messageToUpdate.message.id]) {
          clearTimeout(markAsReadTimers[messageToUpdate.message.id]);
        }

        markAsReadTimers[messageToUpdate.message.id] = setTimeout(() => {
          if (messageToUpdate.isInView) {
            markMessageAsRead(messageToUpdate);
          }
        }, MARK_AS_READ_TIMEOUT);
      }
    }

    async function markMessageAsRead(messageState: MessageState): Promise<void> {
      if (!user.value) {
        throw new Error('Could not find the user to update.');
      }

      await api.conversationMessage.markConversationMessageAsRead({
        conversationId: messageState.message.conversationId,
        messageId: messageState.message.id,
      });

      messageState.message.isRead = true;
    }

    async function addReadMessageStatus(
      conversationId: string,
      messageId: string,
      readMessagesCount: number,
    ): Promise<void> {
      const messageState = getConversationMessage.value(
        conversationId,
        messageId,
      );

      if (!messageState) {
        console.error(
          `Could not find the message to add message read status to  (conversationId: ${conversationId}, messageId: ${messageId}), failing silently...`,
        );

        return;
      }

      messageState.message.readMessagesCount = readMessagesCount;
    }

    function updateReaction(
      messageState: MessageState,
      reaction: Pick<ReactionResultDTO, 'previousReaction' | 'reaction'>,
    ): void {
      const reactionsMap = new Map(
        messageState.message.reactions?.map(({ reaction, count }) => [
          reaction,
          count,
        ]),
      );

      if (reaction.previousReaction) {
        const previousReactionCount
          = reactionsMap.get(reaction.previousReaction) ?? 0;
        if (previousReactionCount <= 1) {
          reactionsMap.delete(reaction.previousReaction);
        }
        else {
          reactionsMap.set(
            reaction.previousReaction,
            previousReactionCount - 1,
          );
        }
      }

      const newReactionCount = reactionsMap.get(reaction.reaction) ?? 0;
      reactionsMap.set(reaction.reaction, newReactionCount + 1);

      messageState.message.reactions = Array.from(reactionsMap.entries()).map(
        ([reaction, count]) => ({
          reaction,
          count,
        }),
      );
    }

    function updateMessageReactions(
      messageState: MessageState,
      aggregatedReactions: ReactionAggregateResultDTO[],
    ): void {
      if (!messageState.message.reactions) {
        messageState.message.reactions = [];
      }

      const isAtBottom = scrollStore.isScrollPositionAtBottom(
        messageState.message.conversationId,
      );

      messageState.message.reactions = aggregatedReactions;

      if (isAtBottom) {
        scrollStore.scrollConversationToBottom(
          messageState.message.conversationId,
          true,
        );
      }
    }

    function addReaction(messageState: MessageState, reactionToAdd: string): void {
      if (!messageState.message.reactions) {
        messageState.message.reactions = [];
      }

      const isAtBottom = scrollStore.isScrollPositionAtBottom(
        messageState.message.conversationId,
      );

      const reactionsMap = new Map(
        messageState.message.reactions?.map(({ reaction, count }) => [
          reaction,
          count,
        ]),
      );

      const reactionCount = reactionsMap.get(reactionToAdd) ?? 0;
      reactionsMap.set(reactionToAdd, reactionCount + 1);
      messageState.message.reactions = Array.from(reactionsMap.entries()).map(
        ([reaction, count]) => ({
          reaction,
          count,
        }),
      );

      if (isAtBottom) {
        scrollStore.scrollConversationToBottom(
          messageState.message.conversationId,
          true,
        );
      }
    }

    function deleteReaction(
      conversationId: string,
      messageId: string,
      reactionString: string,
    ): void {
      const messageState = getConversationMessage.value(
        conversationId,
        messageId,
      );

      if (!messageState) {
        return;
      }

      messageState.message.reactions = messageState.message.reactions
        ?.map((reactionAggregate) => {
          if (reactionAggregate.reaction === reactionString) {
            const newCount = reactionAggregate.count - 1;
            return {
              ...reactionAggregate,
              count: newCount,
            };
          }

          return reactionAggregate;
        })
        .filter(reactionAggregate => reactionAggregate.count > 0);
    }

    async function sendMessageReaction(
      messageState: MessageState,
      reactionString: string,
    ): Promise<void> {
      if (!user.value) {
        throw new Error('Tried to react without a loaded user.');
      }

      const existingReaction = messageState.message.ownReaction;

      const { conversationId, id: messageId } = messageState.message;

      const handleAddReaction = async (): Promise<void> => {
        addReaction(messageState, reactionString);

        const reactionPayload: ReactionCreatePayload = {
          reaction: reactionString,
        };

        const response = await api.unwrap(
          api.conversationMessage.reactToConversationMessage({
            conversationId,
            messageId,
            reactionCreatePayload: reactionPayload,
          }),
        );

        const data = response.data;
        if (!data) {
          deleteReaction(conversationId, messageId, reactionString);

          throw new Error('Did not receive a response from the server.');
        }

        messageState.message.ownReaction = reactionString;
      };

      const handleUpdateReaction = async (existingReactionString: string): Promise<void> => {
        if (!user.value) {
          throw new Error('Tried to update reaction without a loaded user.');
        }
        const reactionPayload: ReactionCreatePayload = {
          reaction: reactionString,
        };

        updateReaction(messageState, {
          reaction: reactionString,
          previousReaction: existingReactionString,
        });

        const response = await api.unwrap(
          api.conversationMessage.updateConversationMessageReaction({
            conversationId,
            messageId,
            reactionCreatePayload: reactionPayload,
          }),
        );

        const data = response.data;

        if (!data) {
          updateReaction(messageState, {
            reaction: existingReactionString,
            previousReaction: reactionString,
          });

          throw new Error('Did not receive a response from the server.');
        }

        messageState.message.ownReaction = reactionString;
      };

      const handleDeleteReaction = async (reactionToDelete: string): Promise<void> => {
        deleteReaction(conversationId, messageId, reactionToDelete);

        try {
          await api.conversationMessage.deleteConversationMessageReactions({
            conversationId,
            messageId,
          });
        }
        catch (error) {
          console.error('Error deleting reaction:', error);

          addReaction(messageState, reactionToDelete);
        }
      };

      if (existingReaction === reactionString) {
        handleDeleteReaction(existingReaction);
      }
      else if (existingReaction) {
        handleUpdateReaction(existingReaction);
      }
      else {
        handleAddReaction();
      }

      messageState.message.ownReaction = undefined;
    }

    function updateMessage(
      messageToChange: MessageInput,
      updatedMessage: MessageResultDTO,
    ): void {
      const messageState = messageToState(messageToChange);

      if (!messageState) {
        throw new Error('Could not find the message to update.');
      }

      messageState.message = convertToMessageResultV2({
        ...updatedMessage,
      });
    }

    async function deleteMessage(messageState: MessageState): Promise<void> {
      conversationStore.deleteMessageFromConversation(
        messageState.message.conversationId,
        messageState.message.id,
      );

      await api.conversationMessage.deleteConversationMessage({
        conversationId: messageState.message.conversationId,
        messageId: messageState.message.id,
      });
    }

    function messageToState(message: MessageInput): MessageState | undefined {
      if ('id' in message) {
        return getConversationMessage.value(message.conversationId, message.id);
      }

      return message;
    }

    return {
      activeMessageActionsId,
      editingMessage,
      quoteMessage,
      getConversationMessage,
      openContextMenuId,
      setMessageIsInView,
      markMessageAsRead,
      updateReaction,
      updateMessageReactions,
      addReaction,
      deleteReaction,
      sendMessageReaction,
      addReadMessageStatus,
      deleteMessage,
      createMessage,
      editMessage,
      isEditingConversation,
      setActiveMessageActionsId,
      setOpenContextMenuId,
      updateMessage,
    };
  },
);
